import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';

interface UIContextValue {
  /** Desktop sidebar collapsed to an icon rail. */
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  /** Mobile off-canvas nav drawer. */
  mobileNavOpen: boolean;
  setMobileNavOpen: (open: boolean) => void;
  /** Command palette. */
  commandOpen: boolean;
  setCommandOpen: (open: boolean) => void;
}

const UIContext = createContext<UIContextValue | null>(null);

export function UIProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage('nh.sidebarCollapsed', false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);

  const toggleSidebar = useCallback(
    () => setSidebarCollapsed(!sidebarCollapsed),
    [sidebarCollapsed, setSidebarCollapsed],
  );

  const value = useMemo(
    () => ({ sidebarCollapsed, toggleSidebar, mobileNavOpen, setMobileNavOpen, commandOpen, setCommandOpen }),
    [sidebarCollapsed, toggleSidebar, mobileNavOpen, commandOpen],
  );
  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI(): UIContextValue {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used within UIProvider');
  return ctx;
}
